/** Calendar-day keys, in the device's own timezone. */
export type DateKey = string; // YYYY-MM-DD

export function toDateKey(d: Date): DateKey {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayKey(now: Date = new Date()): DateKey {
  return toDateKey(now);
}

/**
 * A well-formed key that names the day it says it does.
 *
 * `fromDateKey` is deliberately lax — it rolls `2026-02-30` over into March
 * rather than failing — which is right for a key the app wrote itself and
 * wrong for one that arrived in a URL. The round trip is what catches it.
 */
export function isDateKey(value: string | null | undefined): value is DateKey {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return toDateKey(fromDateKey(value)) === value;
}

/**
 * A day a record can be written to: real, and not still to come.
 *
 * Keys sort lexicographically, so the whole of the future check is a string
 * comparison. `today` is the prayer-day, which before Fajr is yesterday's key
 * — so between midnight and Fajr the calendar date counts as ahead, which is
 * the same boundary everything else here measures from.
 */
export function isRecordableDay(
  value: string | null | undefined,
  today: DateKey,
): value is DateKey {
  return isDateKey(value) && value <= today;
}

export function fromDateKey(key: DateKey): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function addDays(key: DateKey, delta: number): DateKey {
  const d = fromDateKey(key);
  d.setDate(d.getDate() + delta);
  return toDateKey(d);
}

/** Inclusive list of day keys, oldest first. */
export function dateRange(from: DateKey, to: DateKey): DateKey[] {
  const out: DateKey[] = [];
  for (let k = from; k <= to; k = addDays(k, 1)) out.push(k);
  return out;
}
